import type { Backend } from '@/lib/backend-cors/backend.ts'
import type { CreateImageCollectionResponse, CreateImageResponse } from '@/lib/backend-cors/types.ts'
import type { Component, ImageCollection, InputImage, ItemCompound } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
import APIError from '@/lib/APIError.ts'
import { useLogsStore } from '@/stores/log.ts'

const convertError = (backend: Backend, e: any) => {
	const parsed = backend.parseError(e)
	return new APIError(parsed.message, parsed.code || 0)
}

/**
 * Upload an image collection and convert any errors into an APIError
 *
 * @param backend       The backend to use
 * @param token         API token
 * @param semantic      Image collection semantic
 * @param metadata      Image collection metadata
 * @param collection    The collection itself
 *
 * @returns {Promise<CreateImageCollectionResponse>}
 */
export const addImageCollection = async (backend: Backend, token: string, semantic: string|null, metadata: Record<string, any>, collection: ImageCollection): Promise<CreateImageCollectionResponse> => {
	try {
		useLogsStore().info('Creating image collection ' + collection.id, metadata)
		
		const response = await backend.createImageCollection(token, collection.title, semantic, metadata)
		useLogsStore().info(collection.id + ' -> ' + response.hash_id)
		
		return response
	} catch (e: any) {
		const parsed = convertError(backend, e)
		useLogsStore().error('Error creating image collection', parsed.message)
		
		throw parsed
	}
}

/**
 * Upload an image into a collection and convert any errors into an APIError
 *
 * @param backend       The backend to use
 * @param token         API token
 * @param collectionID  Image Collection ID
 * @param image         Image
 *
 * @returns {Promise<CreateImageResponse>}
 */
export const addImage = async (backend: Backend, token: string, collectionID: string|number, image: InputImage): Promise<CreateImageResponse> => {
	try {
		useLogsStore().info('Uploading image ' + image.id)
		
		const response = await backend.uploadImage(token, collectionID, image.base64 as string, image.title ?? null)
		useLogsStore().info(image.id + ' -> ' + response.hash_id)
		
		return response
	} catch (e: any) {
		const parsed = convertError(backend, e)
		useLogsStore().error('Error creating image collection', parsed.message)
		
		throw parsed
	}
}

/**
 * Delete an image collection
 *
 * @param backend       The backend to use
 * @param token         API token
 * @param collectionID  Image collection to delete
 */
export const deleteImageCollection = async (backend: Backend, token: string, collectionID: string|number): Promise<void> => {
	try {
		useLogsStore().info('Deleting image collection ' + collectionID)
		return await backend.deleteImageCollection(token, collectionID)
	} catch (e: any) {
		throw convertError(backend, e)
	}
}

/**
 * Delete an image
 *
 * @param backend       The backend to use
 * @param token         API token
 * @param collectionID  Image collection of the image
 * @param imageID       Image to delete
 */
export const deleteImage = async (backend: Backend, token: string, collectionID: string|number, imageID: string|number): Promise<void> => {
	try {
		useLogsStore().info('Deleting image ' + imageID)
		return await backend.deleteImage(token, collectionID, imageID)
	} catch (e: any) {
		throw convertError(backend, e)
	}
}

export const updateImageCollection = async (backend: Backend, token: string, collectionID: string|number, metadata: Record<string, any> = {}): Promise<void> => {
	try {
		useLogsStore().info('Updating image collection ' + collectionID, metadata)
		return await backend.updateImageCollection(token, collectionID, null, metadata)
	} catch (e: any) {
		const parsed = convertError(backend, e)
		useLogsStore().error('Error updating image collection', parsed.message)
		
		throw parsed
	}
}

/**
 * Upload and update image collections in the backend.
 * Returns a bunch of promises, use {@link Promise.all} to wait for all of them to be finished
 */
export const uploadImageCollections = (backend: Backend, token: string, component: Component, item: ItemCompound, metadata: Record<string, any>, imageCollections: ImageCollection[]): Promise<any>[] => {
	let promises: Promise<any>[] = []
	
	for (let imageCollection of imageCollections) {
		// Check if deleted
		if (imageCollection.deleted) {
			// Delete image collection itself.
			let imageCollectionPromise = deleteImageCollection(backend, token, imageCollection.hash_id!).then(() => {
				if (item) {
					if (item.image_collections) {
						for (let i = 0; i < item.image_collections.length; i++) {
							if (item.image_collections[i].id == imageCollection.id) {
								item.image_collections.splice(i, 1)
								break
							}
						}
					}
				} else if (Array.isArray(component.properties.modelValue)) {
					for (let i = 0; i < component.properties.modelValue.length; i++) {
						if (component.properties.modelValue[i].id == imageCollection.id) {
							component.properties.modelValue.splice(i, 1)
							break
						}
					}
				}
			})
			
			promises.push(imageCollectionPromise)
		} else {
			// Check if already uploaded
			if (!imageCollection.hash_id && imageCollection.images.length) {
				// Create Image Collection and its images
				const semantic = Array.isArray(component.properties.semantic) ?
				                 (component.properties.semantic[0] ?? null) :
				                 (component.properties.semantic ?? null)
				
				let imageCollectionPromise = addImageCollection(backend, token, semantic, metadata, imageCollection).then(result => new Promise((resolve, reject) => {
					imageCollection.hash_id = result.hash_id
					
					let imagePromises = imageCollection.images
					                                   .filter(i => i.base64)
					                                   .map(image => addImage(backend, token, imageCollection.hash_id!, image).then(result => {
						                                   image.hash_id = result.hash_id
						                                   image.url = result.url
						                                   image.base64 = null
						                                   
						                                   return image
					                                   }))
					
					return Promise.all(imagePromises).then(resolve).catch(reject)
				}))
				
				promises.push(imageCollectionPromise)
			} else {
				let imageCount = imageCollection.images.length
				
				// Check whether the images were uploaded or deleted
				for (let image of imageCollection.images) {
					if (image.deleted) {
						let imagePromise = deleteImage(backend, token, imageCollection.hash_id!, image.hash_id!).then(() => {
							for (let i = 0; i < imageCollection.images.length; i++) {
								if (imageCollection.images[i].id == image.id) {
									imageCollection.images.splice(i, 1)
									break
								}
							}
						})
						
						promises.push(imagePromise)
						imageCount--
					} else if (!image.hash_id) {
						let imagePromise = addImage(backend, token, imageCollection.hash_id!, image).then(result => {
							image.hash_id = result.hash_id
							image.url = result.url
							image.base64 = null
							
							return image
						})
						
						promises.push(imagePromise)
						imageCount++
					}
				}
				
				/* // If we have 0 images now, we delete the collection for housekeeping
				 if (imageCount <= 0) {
				 promises.push(deleteCollection(item, imageCollection))
				 } */
			}
		}
	}
	
	return promises
}